/* 213A · editable interface strings.
   The island is optional and every lookup carries the original literal as its
   fallback, so with no override the byte on screen is the byte P6-R4 shipped. */
const TOS_UI_STRINGS = (() => {
  try {
    const el = typeof document === "undefined" ? null : document.getElementById("tos-ui-strings");
    return el ? JSON.parse(el.textContent || "{}") : {};
  } catch (error) { return {}; }
})();
const TOS_UI = (key, fallback) => {
  const value = TOS_UI_STRINGS[key];
  return typeof value === "string" && value !== "" ? value : fallback;
};
const TOS_UI_T = (key, pattern, args) => TOS_UI(key, pattern)
  .replace(/\{(\d+)\}/g, (whole, index) => (args[Number(index)] === undefined ? whole : String(args[Number(index)])));
/* 208B · generated Shopify route bridge. The passage controller used to
   read the route out of a local file name; on a storefront the path has
   no file name, so the map below and `<body data-route>` carry it. */
const R1C_ROUTE_PATHS = {
  "404": null,
  "index": "/",
  "picual": "/pages/picual",
  "formate": "/pages/formate",
  "poveste": "/pages/despre-noi",
  "jurnal": "/blogs/news",
  "jurnal-article": "/blogs/news",
  "product": "/products/3-olives-society-picual",
  "cart": "/cart",
  "search": "/search",
  "cont": "/account",
  "contact": "/pages/contact",
  "faq": "/pages/intrebari-frecvente",
  "society": "/pages/society",
  "livrare": "/policies/shipping-policy",
  "retur": "/policies/refund-policy",
  "termeni": "/policies/terms-of-service",
  "confidentialitate": "/policies/privacy-policy",
  "cookies": "/pages/cookies",
  "anpc-sal": "/pages/anpc-sal"
};
/* First declaration wins: /blogs/news is the journal index, and the
   article route only borrows that path as a fallback destination. */
const R1C_PATH_TO_ROUTE = new Map();
for (const [id, path] of Object.entries(R1C_ROUTE_PATHS)) {
  if (!path) continue;
  const key = path.split("#")[0].replace(/\/+$/, "") || "/";
  if (!R1C_PATH_TO_ROUTE.has(key)) R1C_PATH_TO_ROUTE.set(key, id);
}
const r1cCleanPath = (pathname) => pathname.replace(/\/+$/, "") || "/";
import { scheduleBoot } from "./3os-living-r1c-p1-boot-gate.js";
import {
  createPassageArrivalController,
  createPassageNavigationGate,
  ensurePassageDepartureVisible,
  isFreshPassage,
  resetPassageCurtain,
  sameRoutePath,
  shouldResetPassageOnPageShow,
} from "./3os-route-passage-controller-228c.js";
import { withPassageRelease, withoutPassageRelease } from "./3os-route-passage-release-228d.js";
/* 207A · Arrival & Continuity — The Golden Table Line */

const root = document.documentElement;
/* 229B: scriptul inline din head ridică clasa înainte de primul paint ca un
   pasaj proaspăt să sosească acoperit; fiecare drum de cod o eliberează. */
const releasePassageHold = () => root.classList.remove("r1c-passage-hold");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const INTRO_KEY = "3os:arrival:intro:v1";
const PASSAGE_KEY = "3os:arrival:passage:v1";
const PASSAGE_RELEASE = "228d";
/* 225A — dedicated tight-crop, porcelain-white, lossless-alpha derivative.
   It carries enough real pixels for the 184px maximum at DPR 3 and needs no
   CSS recolouring, so alpha fringe can no longer become a white matte. */
const BRAND_LOGO = new URL("./3os-route-logo-white-lossless-228n.svg", import.meta.url).href;

/* `label` is the route's name and is what the passage curtain announces and
   what `.arrival-context__section` falls back to — it never gets shortened.
   `shortLabel` is optional and exists for one place only: the red context chip
   in the header, which below 700px is a 92px lane between the logo and the
   cart badge (and is hidden outright below 370px). Measured at 390
   (10px/800/0.16em uppercase): TOS_UI("ui_confidentialitate", `Confidențialitate`) asks for 156.1px and,
   having no break opportunity, painted 260.2px² straight under the cart
   trigger. §6's guard clips the lane instead, but an ellipsis is a guard rail,
   not a design — so every label that would need one is given a short form
   here. Each short label is a word the page itself already uses. */
const ROUTES = {
  index: {
    label: TOS_UI("ui_acasa", "Acasă"),
    next: "picual",
    nextTitle: TOS_UI("ui_incepe_cu_picual", "Începe cu Picual."),
    nextCopy: TOS_UI("ui_descopera_soiul_spaniol_ales_pentru_gust", "Descoperă soiul spaniol ales pentru gust, caracter și energie de fiecare zi."),
  },
  picual: {
    label: "Picual",
    next: "formate",
    nextTitle: TOS_UI("ui_doua_ritmuri_acelasi_picual", "Două formate. Un singur caracter."),
    nextCopy: TOS_UI("ui_500_ml_pentru_fiecare_zi_1", "500 ml pentru fiecare zi. 1 L pentru mesele împărțite."),
  },
  formate: {
    label: "Formate",
    next: "product",
    nextTitle: TOS_UI("ui_alege_sticla_ta", "Alege sticla ta."),
    nextCopy: TOS_UI("ui_vezi_produsul_selecteaza_formatul_si_du", "Vezi produsul, selectează formatul și du primul ingredient în bucătăria ta."),
  },
  product: {
    label: "Produs",
    next: "poveste",
    nextTitle: TOS_UI("ui_trei_prieteni_un_prim_ingredient", "Trei prieteni. Un prim ingredient."),
    /* 207-FINAL-R1A: the public passage named Jaén as an origin. Jaén is not
       evidenced as the agricultural origin and stays internal-only (see the
       founders slot map in content-modes.js). Spain is the canonical public
       origin, so the sentence keeps its rhythm and loses the claim. */
    nextCopy: TOS_UI("ui_din_transilvania_si_istanbul_pana_in", "Din Transilvania și Istanbul până în Spania — află de ce a început Society."),
  },
  poveste: {
    label: "Povestea",
    next: "society",
    nextTitle: TOS_UI("ui_masa_continua", "Energia continuă."),
    nextCopy: TOS_UI("ui_sport_gatit_si_oameni_reali_locul", "Sport, gătit și oameni reali — locul în care produsul devine apartenență."),
  },
  society: {
    label: "Society",
    next: "jurnal",
    nextTitle: TOS_UI("ui_din_bucataria_society", "Din bucătăria Society."),
    nextCopy: TOS_UI("ui_gesturi_simple_mese_impartite_si_idei", "Gesturi simple, energie împărțită și idei care încep cu primul ingredient."),
  },
  jurnal: {
    label: "Jurnal",
    next: "contact",
    nextTitle: TOS_UI("ui_intra_in_vorba_cu_noi", "Intră în vorbă cu noi."),
    nextCopy: TOS_UI("ui_ai_o_intrebare_despre_picual_comanda", "Ai o întrebare despre Picual, comandă sau Society? Suntem aici."),
  },
  contact: {
    label: "Contact",
    next: "index",
    nextTitle: TOS_UI("ui_revino_la_masa", "Revino în bucătărie."),
    nextCopy: TOS_UI("ui_continua_experienta_3_olives_society_de", "Continuă experiența 3 Olives Society de la primul ingredient."),
  },
  "jurnal-gatit": { label: TOS_UI("ui_jurnal_gatit", "Jurnal · Gătit"), shortLabel: TOS_UI("ui_gatit", "Gătit") },
  "jurnal-impreuna": { label: TOS_UI("ui_jurnal_impreuna", "Jurnal · Împreună"), shortLabel: TOS_UI("ui_impreuna", "Împreună") },
  "jurnal-pastrare": { label: TOS_UI("ui_jurnal_pastrare", "Jurnal · Păstrare"), shortLabel: TOS_UI("ui_pastrare", "Păstrare") },
  search: { label: TOS_UI("ui_cautare", "Căutare") },
  cart: { label: TOS_UI("ui_cosul_casei", "Coșul casei"), shortLabel: TOS_UI("ui_cosul", "Coșul") },
  cont: { label: "Cont" },
  faq: { label: "Ajutor" },
  livrare: { label: "Livrare" },
  retur: { label: "Retur" },
  termeni: { label: "Termeni" },
  /* The brief recommends `Datele tale`. Measured in the chip's own type it is
     99.4px against a 92px lane, and the lane cannot grow: at 390 the logo ends
     at x=144 and the cart trigger starts at x=284.4, so a centred lane caps at
     102px and `Datele tale` would leave zero clearance. It would therefore
     ship as TOS_UI("ui_datele_ta", `DATELE TA…`) on every phone width — the defect this slice exists
     to remove. `Date` is the page's own noun (eyebrow: TOS_UI("ui_politici_date_personale", `Politici · Date
     personale`)), it matches the single-noun register of its legal siblings
     (Termeni · Cookies · Retur · Livrare) and it fits with 52px to spare. */
  confidentialitate: { label: TOS_UI("ui_confidentialitate", "Confidențialitate"), shortLabel: "Date" },
  cookies: { label: "Cookies" },
  "anpc-sal": { label: TOS_UI("ui_anpc_sal", "ANPC / SAL") },
  "404": { label: TOS_UI("ui_pagina_nu_a_fost_gasita", "Pagina nu a fost găsită"), shortLabel: "404" },
};

const readSession = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* Storage can be unavailable in privacy modes; the experience still works. */
  }
};

const removeSession = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* No persistence is still a valid fallback. */
  }
};

const routeFromPath = (pathname = window.location.pathname) =>
  R1C_PATH_TO_ROUTE.get(r1cCleanPath(decodeURIComponent(pathname))) || "";

/* 209A · the homepage document calls itself "home" because the approved
   cascade selects on that string; the passage map calls the same surface
   "index". Both are true, and this is the one place that says so. */
const R1C_BODY_ROUTE_ALIAS = {"home":"index"};
const bodyRoute = document.body?.dataset?.route || "";
const currentRoute = R1C_BODY_ROUTE_ALIAS[bodyRoute] || bodyRoute || routeFromPath();
const currentMeta = ROUTES[currentRoute] || {
  label: document.title.split("—")[0].trim() || "3 Olives Society",
};

const routeMetaFromUrl = (url) => {
  const key = routeFromPath(url.pathname);
  return ROUTES[key] || { label: "Următorul capitol" };
};

const makeCurtain = () => {
  const curtain = document.createElement("div");
  curtain.className = "arrival-curtain";
  curtain.setAttribute("aria-hidden", "true");
  curtain.innerHTML = `
    <div class="arrival-curtain__inner">
      <img src="${BRAND_LOGO}" alt="" width="1024" height="304" />
      <span class="arrival-curtain__eyebrow" data-arrival-curtain-eyebrow>Următorul capitol</span>
      <strong class="arrival-curtain__title" data-arrival-curtain-title>${currentMeta.label}</strong>
      <i class="arrival-curtain__line"></i>
    </div>`;
  document.body.append(curtain);
  return curtain;
};

const curtain = makeCurtain();
const curtainEyebrow = curtain.querySelector("[data-arrival-curtain-eyebrow]");
const curtainTitle = curtain.querySelector("[data-arrival-curtain-title]");

const setCurtainCopy = (title, eyebrow = "Următorul capitol") => {
  if (curtainTitle) curtainTitle.textContent = title;
  if (curtainEyebrow) curtainEyebrow.textContent = eyebrow;
};

const passageArrival = createPassageArrivalController({
  enter: () => root.classList.add("is-route-arriving"),
  depart: () => root.classList.add("is-route-arrived"),
  reset: () => resetPassageCurtain(root, curtain),
  schedule: (callback, delay) => window.setTimeout(callback, delay),
  clearSchedule: (handle) => window.clearTimeout(handle),
  requestFrame: (callback) => window.requestAnimationFrame(callback),
  cancelFrame: (handle) => window.cancelAnimationFrame(handle),
});

const playArrival = (title, eyebrow = TOS_UI("ui_bine_ai_venit_la_masa", "Bine ai venit în Society")) => {
  if (reducedMotion.matches) return;
  setCurtainCopy(title, eyebrow);
  passageArrival.play();
};

const setupArrival = () => {
  const introMode = new URLSearchParams(window.location.search).get("intro");
  const hasHomeLoader = Boolean(document.querySelector("[data-loader]"));
  const rawPassage = readSession(PASSAGE_KEY);
  let passage = null;

  if (rawPassage) {
    removeSession(PASSAGE_KEY);
    try {
      passage = JSON.parse(rawPassage);
    } catch {
      passage = null;
    }
  }

  const passageIsFresh = passage && isFreshPassage(passage.at);
  if (passageIsFresh) {
    writeSession(INTRO_KEY, "seen");
    playArrival(currentMeta.label, TOS_UI("ui_ai_ajuns_la", "Ai ajuns la"));
    /* 229B: cortina a sărit la poziție sub hold în acest frame; predă
       acoperirea de la câmpul de hold la cortină în frame-ul următor. */
    window.requestAnimationFrame(releasePassageHold);
    return;
  }
  releasePassageHold();

  const seen = readSession(INTRO_KEY) === "seen";
  if (introMode === "0") {
    writeSession(INTRO_KEY, "seen");
    return;
  }

  if (hasHomeLoader) {
    /* main.js owns the full illustrated arrival AND records the visit itself
       the moment it decides to play it. R1-FIX-06: writing the key here too
       was a race — in the bundled build this module executes BEFORE main.js,
       so the redundant write made main.js skip the intro on every first
       visit. 207A simply stands down on the home route. */
    return;
  }

  if (!seen || introMode === "1") {
    writeSession(INTRO_KEY, "seen");
    playArrival(currentMeta.label);
  }
};

const setupGoldenLine = () => {
  const header = document.querySelector(".site-header");
  if (!header) return;
  let progress = header.querySelector("[data-scroll-progress]");

  if (!progress) {
    progress = document.createElement("span");
    progress.className = "scroll-progress";
    progress.dataset.scrollProgress = "";
    progress.setAttribute("aria-hidden", "true");
    header.append(progress);
  }

  if (!progress.querySelector(".arrival-progress__node")) {
    const node = document.createElement("i");
    node.className = "arrival-progress__node";
    node.setAttribute("aria-hidden", "true");
    progress.append(node);
  }

  const context = document.createElement("div");
  context.className = "arrival-context";
  context.setAttribute("aria-hidden", "true");
  /* The chip takes the short form when the route declares one; the section
     line keeps the full label, exactly like the curtain. */
  context.innerHTML = `
    <span class="arrival-context__route">${currentMeta.shortLabel || currentMeta.label}</span>
    <span class="arrival-context__section" data-arrival-section>${currentMeta.label}</span>`;
  header.append(context);

  let raf = 0;
  const updateProgress = () => {
    raf = 0;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = max > 0 ? Math.min(Math.max(window.scrollY / max, 0), 1) : 0;
    root.style.setProperty("--arrival-progress", ratio.toFixed(4));
  };

  const requestUpdate = () => {
    if (!raf) raf = requestAnimationFrame(updateProgress);
  };

  updateProgress();
  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate, { passive: true });

  const sectionLabel = context.querySelector("[data-arrival-section]");
  const sections = [...document.querySelectorAll("main > section")].filter((section) =>
    section.querySelector("h1, h2")
  );
  if (!("IntersectionObserver" in window) || !sectionLabel || !sections.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const active = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!active) return;
      const heading = active.target.querySelector("h1, h2");
      if (heading?.textContent) sectionLabel.textContent = heading.textContent.trim();
    },
    { rootMargin: "-28% 0px -54%", threshold: [0, 0.15, 0.35, 0.6] }
  );
  sections.forEach((section) => observer.observe(section));
};

const setupNextChapter = () => {
  if (!currentMeta.next) return;
  /* A chapter whose Shopify resource does not exist yet gets no curtain:
     an invented handle would be a broken promise. 208C turns it on. */
  const nextHref = R1C_ROUTE_PATHS[currentMeta.next];
  if (!nextHref) return;
  const footer = document.querySelector(".tos-footer");
  if (!footer || document.querySelector(".arrival-next")) return;

  const nextMeta = ROUTES[currentMeta.next];
  const section = document.createElement("section");
  section.className = "arrival-next";
  section.setAttribute("aria-labelledby", "arrival-next-title");
  section.innerHTML = `
    <a class="arrival-next__link" href="${nextHref}">
      <span class="arrival-next__inner">
        <span>
          <span class="arrival-next__eyebrow">Următorul capitol · ${nextMeta.label}</span>
          <h2 id="arrival-next-title">${currentMeta.nextTitle}</h2>
        </span>
        <span class="arrival-next__aside">
          <p>${currentMeta.nextCopy}</p>
          <span class="arrival-next__action">Continuă povestea <span class="arrival-next__arrow" aria-hidden="true">→</span></span>
        </span>
      </span>
    </a>`;
  footer.before(section);
};

const eligiblePassageLink = (event, anchor) => {
  if (!anchor || event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  if (anchor.hasAttribute("download") || anchor.target === "_blank") return null;
  if (anchor.matches("[data-menu-open], [data-menu-close], [data-cart-open], [data-consent-open]")) return null;

  const raw = anchor.getAttribute("href");
  if (!raw || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:")) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (!R1C_PATH_TO_ROUTE.has(r1cCleanPath(url.pathname))) return null;
  if (sameRoutePath(window.location.pathname, url.pathname)) return null;
  return url;
};

const setupPassages = () => {
  const cleanHref = withoutPassageRelease(window.location.href);
  if (cleanHref) {
    try { window.history.replaceState(window.history.state, "", cleanHref); } catch { /* navigation remains valid */ }
  }
  const navigationGate = createPassageNavigationGate({
    delay: 330,
    schedule: (callback, delay) => window.setTimeout(callback, delay),
    navigate: (href) => window.location.assign(href),
  });

  document.addEventListener("click", (event) => {
    const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
    const url = eligiblePassageLink(event, anchor);
    if (!url) return;

    if (reducedMotion.matches) return;
    event.preventDefault();
    if (!navigationGate.begin(withPassageRelease(url.href, PASSAGE_RELEASE))) return;

    const destination = routeMetaFromUrl(url);
    setCurtainCopy(destination.label);
    writeSession(PASSAGE_KEY, JSON.stringify({ at: Date.now(), label: destination.label }));
    root.classList.remove("is-route-arriving", "is-route-arrived");
    root.classList.add("is-route-leaving");
    window.setTimeout(() => ensurePassageDepartureVisible(root, curtain, window.innerHeight), 120);
    window.setTimeout(() => ensurePassageDepartureVisible(root, curtain, window.innerHeight, 0.01), 220);
  });

  window.addEventListener("pageshow", (event) => {
    releasePassageHold();
    navigationGate.reset();
    if (shouldResetPassageOnPageShow(event.persisted)) passageArrival.reset();
  });
};

root.classList.add("arrival-207a");
/* R1C-M1-P1 — boot after first paint, own macrotask, same order */
scheduleBoot(() => {
  setupArrival();
  setupGoldenLine();
  setupNextChapter();
  setupPassages();
});
